import React, { useState, useEffect, useMemo } from 'react'
import {
  Search,
  X,
  Users,
  UserCheck,
  Loader2,
  AlertCircle,
  Mail
} from 'lucide-react'
import Tooltip from '../ui/Tooltip'
import { RecipientReference, OrganizationUser } from '../../types/report'
import { PickerContact } from '../../types/contact'
import { reportService } from '../../services/reportService'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'

interface RecipientPickerProps {
  selectedRefs: RecipientReference[]
  onChange: (refs: RecipientReference[]) => void
  activationId?: string
  disabled?: boolean
}

const RecipientPicker: React.FC<RecipientPickerProps> = ({
  selectedRefs,
  onChange,
  activationId,
  disabled = false
}) => {
  const { selectedCustomer } = useGlobalCustomerStore()
  const customerId = selectedCustomer?.id || null

  const [internalUsers, setInternalUsers] = useState<OrganizationUser[]>([])
  const [partnerContacts, setPartnerContacts] = useState<PickerContact[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [loadingContacts, setLoadingContacts] = useState(false)
  const [usersError, setUsersError] = useState<string | null>(null)
  const [contactsError, setContactsError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Fetch internal users
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true)
      setUsersError(null)
      try {
        const response = await reportService.getOrganizationUsers(
          customerId || undefined
        )
        setInternalUsers(response.data.users)
      } catch (error) {
        setUsersError(
          error instanceof Error ? error.message : 'Failed to load users'
        )
      } finally {
        setLoadingUsers(false)
      }
    }
    fetchUsers()
  }, [customerId])

  // Fetch partner contacts when activationId is provided
  useEffect(() => {
    if (!activationId || !customerId) {
      setPartnerContacts([])
      return
    }
    const fetchContacts = async () => {
      setLoadingContacts(true)
      setContactsError(null)
      try {
        const data = await reportService.getActivationContacts(
          activationId,
          customerId
        )
        setPartnerContacts(data)
      } catch (error) {
        setContactsError(
          error instanceof Error ? error.message : 'Failed to load contacts'
        )
      } finally {
        setLoadingContacts(false)
      }
    }
    fetchContacts()
  }, [activationId, customerId])

  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) return internalUsers
    const q = searchQuery.toLowerCase()
    return internalUsers.filter(
      (u) =>
        u.displayName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        u.firstName.toLowerCase().includes(q) ||
        u.lastName.toLowerCase().includes(q)
    )
  }, [internalUsers, searchQuery])

  const filteredContacts = useMemo(() => {
    if (!searchQuery.trim()) return partnerContacts
    const q = searchQuery.toLowerCase()
    return partnerContacts.filter(
      (c) =>
        c.displayName.toLowerCase().includes(q) ||
        c.firstName.toLowerCase().includes(q) ||
        c.lastName.toLowerCase().includes(q) ||
        (c.email?.toLowerCase().includes(q) ?? false)
    )
  }, [partnerContacts, searchQuery])

  const isSelected = (
    type: RecipientReference['type'],
    id: string
  ): boolean => {
    return selectedRefs.some((ref) => ref.type === type && ref.id === id)
  }

  const toggleRecipient = (type: RecipientReference['type'], id: string) => {
    if (isSelected(type, id)) {
      onChange(
        selectedRefs.filter((ref) => !(ref.type === type && ref.id === id))
      )
    } else {
      onChange([...selectedRefs, { type, id }])
    }
  }

  return (
    <div className='bg-gray-50 border border-gray-200 rounded-lg p-4'>
      {/* Header */}
      <div className='flex items-center justify-between mb-3'>
        <label className='block text-xs font-medium text-gray-700'>
          <Mail className='h-3.5 w-3.5 inline mr-1' />
          Email Recipients
        </label>
        {selectedRefs.length > 0 && (
          <span className='inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700'>
            {selectedRefs.length} selected
          </span>
        )}
      </div>

      {/* Search */}
      <div className='relative mb-3'>
        <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400' />
        <input
          type='text'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder='Search recipients...'
          disabled={disabled}
          className='w-full pl-9 pr-8 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed'
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className='absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600'
          >
            <X className='h-3.5 w-3.5' />
          </button>
        )}
      </div>

      {/* Scrollable list */}
      <div className='max-h-64 overflow-y-auto space-y-1'>
        {/* Internal Users Section */}
        <div className='mb-2'>
          <div className='flex items-center space-x-1.5 mb-1.5 px-1'>
            <Users className='h-3 w-3 text-gray-500' />
            <span className='text-xs font-semibold text-gray-600 uppercase tracking-wider'>
              Internal Users
            </span>
          </div>

          {loadingUsers ? (
            <div className='flex items-center space-x-2 py-3 justify-center'>
              <Loader2 className='h-4 w-4 text-blue-500 animate-spin' />
              <span className='text-xs text-gray-500'>Loading users...</span>
            </div>
          ) : usersError ? (
            <div className='text-xs text-red-500 py-2 px-1'>
              <AlertCircle className='h-3 w-3 inline mr-1' />
              {usersError}
            </div>
          ) : filteredUsers.length === 0 ? (
            <p className='text-xs text-gray-400 py-2 px-1'>
              {searchQuery ? 'No users match your search' : 'No users found'}
            </p>
          ) : (
            filteredUsers.map((user) => (
              <label
                key={user.id}
                className='flex items-center space-x-2 cursor-pointer py-1.5 px-1 hover:bg-white rounded'
              >
                <input
                  type='checkbox'
                  checked={isSelected('internal_user', user.id)}
                  onChange={() => toggleRecipient('internal_user', user.id)}
                  disabled={disabled}
                  className='h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed'
                />
                <div className='flex-1 min-w-0'>
                  <span className='text-xs text-gray-900 block truncate'>
                    {user.displayName}
                  </span>
                  <span className='text-xs text-gray-500 block truncate'>
                    {user.email}
                  </span>
                </div>
              </label>
            ))
          )}
        </div>

        {/* Partner Contacts Section */}
        {activationId && (
          <div className='border-t border-gray-200 pt-2 mt-2'>
            <div className='flex items-center space-x-1.5 mb-1.5 px-1'>
              <UserCheck className='h-3 w-3 text-gray-500' />
              <span className='text-xs font-semibold text-gray-600 uppercase tracking-wider'>
                Partner Contacts
              </span>
            </div>

            {loadingContacts ? (
              <div className='flex items-center space-x-2 py-3 justify-center'>
                <Loader2 className='h-4 w-4 text-blue-500 animate-spin' />
                <span className='text-xs text-gray-500'>
                  Loading contacts...
                </span>
              </div>
            ) : contactsError ? (
              <div className='text-xs text-red-500 py-2 px-1'>
                <AlertCircle className='h-3 w-3 inline mr-1' />
                {contactsError}
              </div>
            ) : filteredContacts.length === 0 ? (
              <p className='text-xs text-gray-400 py-2 px-1'>
                {searchQuery
                  ? 'No contacts match your search'
                  : 'No partner contacts found'}
              </p>
            ) : (
              filteredContacts.map((contact) => {
                const hasEmail = !!contact.email
                const row = (
                  <label
                    key={contact.id}
                    className={`flex items-center space-x-2 py-1.5 px-1 rounded ${
                      hasEmail
                        ? 'cursor-pointer hover:bg-white'
                        : 'opacity-60 cursor-not-allowed'
                    }`}
                  >
                    <input
                      type='checkbox'
                      checked={isSelected('partner_contact', contact.id)}
                      onChange={() =>
                        hasEmail &&
                        toggleRecipient('partner_contact', contact.id)
                      }
                      disabled={disabled || !hasEmail}
                      className='h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed'
                    />
                    <div className='flex-1 min-w-0'>
                      <span className='text-xs text-gray-900 block truncate'>
                        {contact.displayName}
                      </span>
                      <span className='text-xs text-gray-500 block truncate'>
                        {contact.email || 'No email'}
                      </span>
                    </div>
                  </label>
                )

                if (!hasEmail) {
                  return (
                    <Tooltip
                      key={contact.id}
                      content='No email address on file'
                      placement='right'
                    >
                      {row}
                    </Tooltip>
                  )
                }

                return <React.Fragment key={contact.id}>{row}</React.Fragment>
              })
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default RecipientPicker
